import { Location } from '@angular/common';
import { Injectable, Injector, NgZone, DOCUMENT, inject } from '@angular/core';
import { SharedPopupService, SharedUtilsService } from '@bepatient/shared';
import { App } from '@capacitor/app';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  AlertController,
  MenuController,
  ModalController,
  Platform,
  ToastController
} from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ROUTER_NAVIGATED,
  ROUTER_NAVIGATION,
  ROUTER_REQUEST,
  RouterRequestAction
} from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { format, fromUnixTime, isBefore } from 'date-fns';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import * as moment from 'moment';
import { resolveCoreLanguage } from './core.i18n';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  forkJoin,
  from,
  fromEvent,
  iif,
  merge,
  MonoTypeOperatorFunction,
  Observable,
  of,
  Subject,
  Subscription,
  timer
} from 'rxjs';
import {
  bufferCount,
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  retryWhen,
  scan,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { CoreApiService } from './core-api.service';
import { CoreExternalAppService } from './core-external-app.service';
import {
  CoreHttpActions,
  CoreHttpActionSetError,
  CoreHttpActionSetMaintenanceMode,
  CoreHttpActionsType
} from './core-http.actions';
import { CoreHttpError } from './core-http.models';
import {
  selectorCoreHttpInternetConnection,
  selectorCoreHttpError
} from './core-http.reducer';
import { CorePopupService } from './core-popup.service';
import {
  CoreRouterActionNavigateForward,
  CoreRouterActionNavigateRoot,
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterNavigationIntent
} from './core-router.actions';
import { selectorCoreRouterUrl } from './core-router.reducer';
import { CoreUtils, CoreUtilsService } from './core-utils';
import { BiometricService } from './biometric.service';
import {
  CoreActionJwtHasExpired,
  CoreActionJwtIsValid,
  CoreActionJwtWillExpire,
  CoreActionLaunchChatbot,
  CoreActionLoadInstanceInfoFailed,
  CoreActionLoadInstanceInfoSuccess,
  CoreActionLoadMeasure,
  CoreActionLoadMenu,
  CoreActionLoadMenuFailed,
  CoreActionLoadMenuSuccess,
  CoreActionLoadUserFailed,
  CoreActionLoadUserSuccess,
  CoreActionLogin,
  CoreActionLoginFailed,
  CoreActionLoginSuccess,
  CoreActionLogout,
  CoreActionNavigatedToPageModule,
  CoreActionNavigateToResource,
  CoreActionNavigateToResourceStack,
  CoreActionNavigateToResourceStackPop,
  CoreActionNavigateToSingleResource,
  CoreActionNOOP,
  CoreActionOpenExternalApp,
  CoreActionOpenExternalAppFailed,
  CoreActionOpenExternalAppSuccess,
  CoreActionRegenerateToken,
  CoreActionResetPassword,
  CoreActionResetPasswordFailed,
  CoreActionResetPasswordSuccess,
  CoreActionSetInstanceEnv,
  CoreActions,
  CoreActionSetGroupImage,
  CoreActionsTypes,
  CoreActionUpdateLogin,
  CoreActionUpdateUserLanguage,
  CoreActionGotoNextActivity,
  CoreActionCheckAppMinVersion,
  CoreActionFCMTokenRefresh,
  CoreActionFCMTokenRefreshSuccess,
  CoreActionFCMTokenRefreshFailed,
  CoreActionCheckForJWT,
  CoreActionLogoutSuccess,
  CoreActionLogoutFailed,
  CoreActionLoadInstanceInfo,
  CoreActionSwitchAppEnvironment,
  CoreActionSetInstancesData,
  CoreActionDisagreeTrustedDevice,
  CoreActionOpenExternalLink,
  CoreActionFetchDocumentContent,
  CoreActionSetBiometricSecret,
  CoreActionSetBiometricPassword,
  CoreActionSetCurrentFormId,
  CoreActionFormOpenExternalTarget,
  CoreActionFormOpenExternalTargetInvalid,
  CoreActionRemoveLogin,
  CoreActionSaveDeeplinkFormData,
  CoreActionCheckBattery,
  CoreActionUpdateInstanceAppColor
} from './core.actions';
// import {
//   MyFormActionFormCompleted,
//   MyFormActionSaveCurrentPage
// } from '@bepatient/my-form-store';
import {
  CORE_CONFIG,
  coreAnalyticsEvents,
  CoreMobiResourceType,
  CorePageModule,
  CorePageModuleIDs,
  CoreResourceStackItem,
  ENVIRONMENT,
  Environment,
  CORE_BACKEND,
  CoreBackendIDs
} from './core.models';
import {
  selectCoreExtraAppConfiguration,
  selectCoreInstanceDistinctUntilChanged,
  selectCoreLoginDistinctUntilChanged,
  selectCorePageModule,
  selectorCoreChatbotAppConfiguration,
  selectorCoreFCMToken,
  selectorCoreInstanceInfo,
  selectorCoreInstancesData,
  selectorCoreLoadedFormData,
  selectorCoreIsAppActive,
  selectorCoreLogin,
  selectorCoreMainNavigationData,
  selectorCoreNavigateToResourceStack,
  selectorCoreNavigateToResourceStackLastItemType,
  selectorCorePageModules,
  selectorCorePlatformInitialized,
  selectorCoreRootPageModuleID,
  selectorCoreSelectedInstance,
  selectorLowBatteryCheckComplete
} from './core.reducer';
import { setDefaultOptions } from 'date-fns';
import {
  enUS,
  fr,
  es,
  fi,
  nl,
  nlBE,
  de,
  hr,
  hu,
  enAU,
  enCA,
  enGB,
  enNZ,
  frCA,
  deAT,
  pl,
  pt,
  ptBR,
  ro,
  sv,
  zhCN,
  zhTW,
  ja,
  it
} from 'date-fns/locale';
import { BatteryAlertService } from './services/battery-alert.service';
import { BatteryUtilsService } from './services/battery-utils.service';

function retryWithDelay<T>(
  time: number,
  count = 1
): MonoTypeOperatorFunction<T> {
  return input =>
    input.pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((acc, error) => ({ count: acc.count + 1, error }), {
            count: 0,
            error: undefined as any
          }),
          tap(current => {
            if (current.count >= count) {
              throw current.error;
            }
          }),
          delay(time)
        )
      )
    );
}

export const enum JWTPopupID {
  WILL_EXPIRE = 'Popup will expire',
  HAS_EXPIRED = 'Popup has expired',
  IS_VALID = 'Session is valid'
}

@Injectable()
export class CoreEffects {
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private injector = inject(Injector);
  private platform = inject(Platform);
  private location = inject(Location);
  private translate = inject(TranslateService);
  private popupService = inject(CorePopupService);
  private menuController = inject(MenuController);
  private coreApiService = inject(CoreApiService);
  private coreUtilsService = inject(CoreUtilsService);
  private biometric = inject(BiometricService);
  private batteryUtilsService = inject(BatteryUtilsService);
  private batteryAlertService = inject(BatteryAlertService);
  private sharedPopupService = inject(SharedPopupService);
  private coreExternalAppService = inject(CoreExternalAppService);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private document = inject<HTMLDocument>(DOCUMENT);
  private environment = inject<Environment | Environment>(ENVIRONMENT);
  private sharedUtilsService = inject(SharedUtilsService);
  private modalController = inject(ModalController);
  private ngZone = inject(NgZone);

  private jwtWillExpireAlert: HTMLIonAlertElement;
  private jwtHasExpiredAlert: HTMLIonAlertElement;
  private loginSubscription$: Subscription;

  // SMARTAPP-195 - Using tokenValidationCheckFrequency and warning duration data from localStorage
  private jwtCheckFrequency =
    JSON.parse(localStorage.getItem('core')) &&
    JSON.parse(localStorage.getItem('core')).login
      ? JSON.parse(localStorage.getItem('core')).login
          .tokenValidationCheckFrequency * 1000
      : 60000;
  private jwtWarningExpirationDuration =
    JSON.parse(localStorage.getItem('core')) &&
    JSON.parse(localStorage.getItem('core')).login
      ? JSON.parse(localStorage.getItem('core')).login
          .tokenWarningExpirationDuration / 60
      : 30;

  // idle$ to emit user idle status true/false
  private idle$: Subject<boolean> = new BehaviorSubject(false);
  // Initally set isIdle as false, user active
  isIdle = false;
  // Set idle after 'idleAfterSeconds' seconds of inactivatiy
  private idleAfterSeconds = 60;
  // For idle status check countdown timer
  private countDown;

  mainNavigation$: Observable<CoreActions | CoreRouterActions> = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(ROUTER_NAVIGATION)),
        this.actions$.pipe(ofType(CoreActionsTypes.APP_INITIALIZED)),
        this.actions$.pipe(ofType(CoreActionsTypes.PLATFORM_INITIALIZED))
      ]).pipe(
        take(1),
        switchMap(() =>
          this.store$.pipe(
            select(selectorCoreMainNavigationData),
            distinctUntilChanged((a, b) => {
              // Ignore the theme-adjusted instanceInfo.appColor: it re-computes
              // on every theme toggle and must not re-trigger navigation. The
              // shared selector keeps appColor intact for other consumers; we
              // only strip it here, where equality drives navigation.
              const omitAppColor = (data: typeof a) =>
                data && data.instanceInfo
                  ? {
                      ...data,
                      instanceInfo: (({ appColor, ...rest }) => rest)(
                        data.instanceInfo
                      )
                    }
                  : data;
              return (
                JSON.stringify(omitAppColor(a)) ===
                JSON.stringify(omitAppColor(b))
              );
            }),
            startWith(
              void 0 as {
                menu;
                user;
                login;
                instance;
                instanceInfo;
              }
            ),
            bufferCount(2, 1),
            withLatestFrom(this.store$.pipe(select(selectorCoreRouterUrl)))
          )
        ),
        switchMap(([[previousData, currentData], currentUrl]) => {
          let action$: Observable<CoreActions | CoreRouterActions> = EMPTY;
          let isPrivacyRead = localStorage.getItem('isPrivacyRead') || void 0;
          /**
           * If authenticated user is in user-preference page
           * to avoid user navigation to timeline when language changed from preference page
           */
          if (
            (currentUrl === '/my-account' ||
              currentUrl === '/my-account-user-preference') &&
            currentData &&
            currentData.instanceInfo &&
            currentData.login
          ) {
            action$ = of(new CoreActionNOOP());
            return action$;
          }
          if (
            !previousData &&
            currentData.login &&
            currentData.login.userChangedPassword
          ) {
            action$ = of(
              new CoreActionUpdateLogin({ userChangedPassword: void 0 })
            );
          } else if (
            !previousData &&
            currentData.login &&
            (currentData.login.userIsInOnboarding ||
              currentData.login.userMustEnterOnboarding)
          ) {
            action$ = of(new CoreActionLogout());
          } else if (!currentData.instance) {
            if (isPrivacyRead && isPrivacyRead === 'true') {
              // If user has already read the privacy statement
              action$ = of(
                new CoreRouterActionNavigateToPageModule({
                  id: CorePageModuleIDs.INSTANCE,
                  asRoot: true,
                  pageIndex: 1,
                  intent: previousData
                    ? CoreRouterNavigationIntent.BACK
                    : CoreRouterNavigationIntent.ROOT
                })
              );
            } else {
              // When app opened for the first time
              action$ = of(
                new CoreRouterActionNavigateToPageModule({
                  id: CorePageModuleIDs.INSTANCE,
                  asRoot: true,
                  intent: previousData
                    ? CoreRouterNavigationIntent.BACK
                    : CoreRouterNavigationIntent.ROOT
                })
              );
            }
          } else if (
            currentData.instanceInfo &&
            !currentData.login &&
            !(previousData && previousData.login && !previousData.user)
          ) {
            action$ = of(
              new CoreRouterActionNavigateToPageModule({
                id: CorePageModuleIDs.ONBOARDING,
                asRoot: true,
                intent: previousData
                  ? previousData.user
                    ? CoreRouterNavigationIntent.BACK
                    : CoreRouterNavigationIntent.FORWARD
                  : CoreRouterNavigationIntent.ROOT
              })
            );
          } else if (
            currentData.instanceInfo &&
            currentData.login &&
            currentData.login.userMustChangePassword
          ) {
            // TODO: Not Used - Remove this logic in future if not needed.
            action$ = of(
              new CoreRouterActionNavigateToPageModule({
                id: CorePageModuleIDs.ONBOARDING,
                asRoot: true,
                intent: CoreRouterNavigationIntent.FORWARD,
                pageIndex: 4
              })
            );
          } else if (currentData.user && currentData.menu) {
            if (currentUrl === '/menu') {
              action$ = of(new CoreActionNOOP());
              return action$;
            } else if (!previousData && (currentUrl === '/' || !currentUrl)) {
              action$ = this.store$.pipe(
                select(selectorCoreRootPageModuleID),
                map(
                  rootPageModule =>
                    new CoreRouterActionNavigateToPageModule({
                      id: rootPageModule
                    })
                )
              );
            } else if (
              previousData &&
              !currentData.login.userChangedPassword &&
              previousData.login.iAuth === currentData.login.iAuth
            ) {
              action$ = this.store$.pipe(
                select(selectorCoreRootPageModuleID),
                map(
                  rootPageModule =>
                    new CoreRouterActionNavigateToPageModule({
                      id: rootPageModule,
                      asRoot: true,
                      intent: CoreRouterNavigationIntent.FORWARD
                    })
                )
              );
            }
          } else if (
            currentData &&
            currentData.instance &&
            currentData.instanceInfo &&
            !currentData.login &&
            !currentData.user
          ) {
            action$ = of(
              new CoreRouterActionNavigateToPageModule({
                id: CorePageModuleIDs.ONBOARDING,
                asRoot: true,
                pageIndex: 0,
                intent: CoreRouterNavigationIntent.BACK
              })
            );
          }

          return action$;
        })
      )
  );

  initPopup = createEffect(
    () =>
      timer(0, 500).pipe(
        map(() => this.document.getElementsByTagName('ion-app')),
        filter(
          (htmlCollection: HTMLCollectionOf<any>) =>
            htmlCollection && htmlCollection.item(0)
        ),
        take(1),
        tap(() => this.popupService.init())
      ),
    { dispatch: false }
  );

  dismissPopup = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGOUT),
        tap(() => this.popupService.dismiss())
      ),
    { dispatch: false }
  );

  login$: Observable<CoreActions | CoreHttpActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.LOGIN),
      map((action: CoreActionLogin) => action.payload),
      switchMap(payload =>
        this.coreApiService
          .login(
            payload.login,
            payload.password,
            payload.fingerprint,
            payload.fcmToken,
            payload.deviceType,
            payload.userTimezone,
            payload.appVersion
          )
          .pipe(
            switchMap(res => {
              const parsedResponse = {
                ...res,
                loginId: this.coreUtilsService.textToBase64(payload.login)
              };
              let biometricData = this.biometric.getDeviceBiometricData();
              // Store/refresh the biometric secret after login UNLESS the user
              // explicitly opted out. (Fresh/undecided devices still enrol so
              // the one-time opt-in prompt can appear; opted-out users don't.)
              if (
                biometricData &&
                !biometricData.userOptedOut &&
                biometricData.biometricType !== 'none' &&
                (payload.loginType != 'biometric' ||
                  biometricData.keychainEnrolled != 'true')
              ) {
                return [
                  new CoreActionLoginSuccess(parsedResponse),
                  new CoreActionSetBiometricSecret({
                    username: payload.login,
                    password: payload.password
                  })
                ];
              }
              return of(new CoreActionLoginSuccess(parsedResponse));
            }),
            catchError(error => {
              if (error && error.status === 503) {
                const coreHttpError: CoreHttpError = {
                  message: void 0,
                  name: '',
                  status: void 0,
                  statusText: ''
                };
                return [
                  new CoreHttpActionSetError(coreHttpError),
                  new CoreActionLoginFailed(),
                  new CoreHttpActionSetMaintenanceMode()
                ];
              }
              return of(new CoreActionLoginFailed());
            })
          )
      )
    )
  );

  regenerateToken$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.REGENERATE_TOKEN),
      map((action: CoreActionLogin) => action.payload),
      switchMap(() =>
        this.coreApiService.regenerateToken().pipe(
          map(payload => {
            localStorage.setItem('sessionPopup', '');
            return new CoreActionUpdateLogin({
              iAuth: payload.iauth,
              tokenValidationCheckFrequency:
                payload.token_validation_check_frequency,
              tokenWarningExpirationDuration:
                payload.token_warning_expiration_duration
            });
          }),
          catchError(() => of(new CoreActionLogout()))
        )
      )
    )
  );

  updateLogin$: Observable<CoreActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.UPDATE_LOGIN),
        tap(() => {
          this.jwtCheckFrequency =
            JSON.parse(localStorage.getItem('core')) &&
            JSON.parse(localStorage.getItem('core')).login
              ? JSON.parse(localStorage.getItem('core')).login
                  .tokenValidationCheckFrequency * 1000
              : 60000;
          this.jwtWarningExpirationDuration =
            JSON.parse(localStorage.getItem('core')) &&
            JSON.parse(localStorage.getItem('core')).login
              ? JSON.parse(localStorage.getItem('core')).login
                  .tokenWarningExpirationDuration / 60
              : 30;
        })
      ),
    { dispatch: false }
  );

  resetPassword$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.RESET_PASSWORD),
      map((action: CoreActionResetPassword) => action.payload),
      switchMap(payload => {
        return this.coreApiService.resetPassword(payload).pipe(
          map(() => new CoreActionResetPasswordSuccess()),
          catchError(() => of(new CoreActionResetPasswordFailed()))
        );
      })
    )
  );

  setInstancesData = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CoreActionsTypes.SET_INSTANCE_ENV,
          CoreActionsTypes.SET_INSTANCES_DATA
        ),
        withLatestFrom(this.store$.pipe(select(selectorCoreSelectedInstance))),
        tap(([action, instance]) => {
          // this.googleAnalyticsEventsService.setInstance(
          //   (instance && instance.url) || ''
          // );
        })
      ),
    { dispatch: false }
  );

  setInstanceEnvironment = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.PLATFORM_INITIALIZED),
        map(() => {
          this.store$.dispatch(
            new CoreActionSetInstanceEnv(this.environment.devType)
          );
        }),
        switchMap(() => this.store$.pipe(select(selectorCoreInstancesData))),
        map(instancesData => {
          /**
           *  If new instance data do not match with existing one
           *  Dispatching action to switch app environment
           * */
          const devType = this.environment.devType.toLowerCase();
          if (instancesData && !instancesData[devType]) {
            this.store$.dispatch(new CoreActionSwitchAppEnvironment());
          }
        })
      ),
    { dispatch: false }
  );

  switchAppEnvironment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.SWITCH_APP_ENVIRONMENT),
        map(() => {
          /**
           * Resetting everything after app environment changed
           * Logging out user session from app,
           * Removing instance data & Core Http Error
           * */
          const coreHttpError: CoreHttpError = {
            message: void 0,
            name: '',
            status: void 0,
            statusText: ''
          };
          this.store$.dispatch(new CoreActionLogout());
          this.store$.dispatch(new CoreActionSetGroupImage(void 0));
          this.store$.dispatch(new CoreActionLoadInstanceInfoSuccess(void 0));
          this.store$.dispatch(new CoreActionSetInstancesData(void 0));
          this.store$.dispatch(new CoreHttpActionSetError(coreHttpError));

          // Clearing local storage data
          localStorage.clear();

          // Using default user browser/device language
          const language = window.navigator.language.split('-')[0];
          this.store$.dispatch(
            new CoreActionUpdateUserLanguage({
              language: language,
              rightToLeft: language ? language === 'ar' : false
            })
          );
        })
      ),
    { dispatch: false }
  );

  loadUserOrMenuFailed$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreActionsTypes.LOAD_USER_FAILED,
        CoreActionsTypes.LOAD_MENU_FAILED
      ),
      map(() => new CoreActionLogout())
    )
  );

  loadUser$: Observable<CoreActions> = createEffect(() =>
    this.store$.pipe(
      selectCoreLoginDistinctUntilChanged(),
      filter(
        login =>
          login &&
          !login.userIsInOnboarding &&
          !login.userMustChangePassword &&
          !login.userMustEnterOnboarding
      ),
      switchMap(() =>
        this.coreApiService.loadUser().pipe(
          map(user => {
            const restriction = this.injector.get(CORE_CONFIG).restrictAppTo;
            return (restriction === 'pro' && !user.configuration.isPro) ||
              (restriction === 'patient' && user.configuration.isPro)
              ? new CoreActionLoadUserFailed()
              : new CoreActionLoadUserSuccess(user);
          }),
          catchError(() => of(new CoreActionLoadUserFailed()))
        )
      )
    )
  );

  applyUserLanguage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.UPDATE_USER_LANGUAGE),
        map((action: CoreActionUpdateUserLanguage) => action.payload),
        tap(({ language, rightToLeft }) => {
          const resolved = resolveCoreLanguage(language);
          moment.locale(resolved.momentLocale);
          this.translate.use(resolved.translateLang);

          this.document.dir = rightToLeft ? 'rtl' : 'ltr';
          // Keep <html lang> in sync so CSS `:lang()` selectors (used for
          // language-aware font fallbacks, e.g. Noto Sans SC vs JP for Han
          // ideographs) resolve correctly.
          this.document.documentElement.lang = resolved.translateLang;
          localStorage.setItem('locale', resolved.pickerLocale);
        })
      ),
    { dispatch: false }
  );

  loadMenuAndImageGroup$: Observable<CoreActions> = createEffect(() =>
    this.store$.pipe(
      selectCoreLoginDistinctUntilChanged(),
      filter(
        login =>
          login &&
          !login.userIsInOnboarding &&
          !login.userMustChangePassword &&
          !login.userMustEnterOnboarding
      ),
      switchMap(() =>
        iif(
          () => this.injector.get(CORE_BACKEND) === CoreBackendIDs.SMART,
          this.store$.pipe(
            select(selectorCoreInstanceInfo),
            filter(instanceInfo => !!instanceInfo),
            distinctUntilChanged(
              (a, b) => a.instanceColor === b.instanceColor && a.url === b.url
            )
          ),
          this.coreApiService.loadInstanceInfo(true)
        )
      ),
      map(() => {
        return new CoreActionLoadMenu();
      })
    )
  );

  fetchDocumentContent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.FETCH_DOCUMENT_CONTENT),
        map((action: CoreActionFetchDocumentContent) => action.payload),
        switchMap(data => this.coreApiService.fetchDocument(data))
      ),
    { dispatch: false }
  );

  loadMenu$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOAD_MENU),
        withLatestFrom(
          this.store$.pipe(select(selectorCoreLogin)),
          this.store$.pipe(select(selectorCoreInstanceInfo)),
          ({}, login, instanceInfo) => ({ login, instanceInfo })
        ),
        filter(data => !!(data.login && data.login.iAuth)),
        switchMap(data =>
          this.coreApiService.loadMenu().pipe(
            map(menu => {
              localStorage.setItem('imgData', data.instanceInfo.logo);
              this.store$.dispatch(
                new CoreActionLoadMenuSuccess({
                  ...menu,
                  color: data.instanceInfo.appColor,
                  instanceLogo: data.instanceInfo.logo,
                  mainButtons: menu.mainButtons.map(button => {
                    switch (button.id) {
                      case 'home':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      case 'myMeasures':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      case 'measures':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      case 'myFiles':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      case 'eDiary':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      case 'events':
                        return {
                          ...button,
                          iconColor: data.instanceInfo.appColor
                        };
                      default:
                        return button;
                    }
                  })
                })
              );
              of(new CoreActionSetGroupImage(data.instanceInfo.logo));
            }),
            catchError(() => of(new CoreActionLoadMenuFailed()))
          )
        )
      ),
    { dispatch: false }
  );

  loadInstanceInfo$ = createEffect(() =>
    this.store$.pipe(
      selectCoreInstanceDistinctUntilChanged(),
      filter(instance => !!instance),
      switchMap(() =>
        this.store$.pipe(select(selectorCorePlatformInitialized))
      ),
      switchMap(isPlatformInitialized =>
        isPlatformInitialized
          ? of(new CoreActionLoadInstanceInfo())
          : of(new CoreActionNOOP())
      )
    )
  );

  loadInstance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.LOAD_INSTANCE_INFO),
      map((action: CoreActionLoadInstanceInfo) => action),
      switchMap(action =>
        this.coreApiService.loadInstanceInfo(false).pipe(
          mergeMap(info => {
            const data = {
              ...info,
              isQR: action?.payload?.isQR ?? false
            };
            return [
              new CoreActionLoadInstanceInfoSuccess(data),
              new CoreActionCheckAppMinVersion()
            ];
          }),
          catchError(() => of(new CoreActionLoadInstanceInfoFailed()))
        )
      )
    )
  );

  loadInstanceInfoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOAD_INSTANCE_INFO_SUCCESS),
        map((action: CoreActionLoadInstanceInfoSuccess) => action.payload),
        filter(data => !!(data && data.instanceColor)),
        tap(data => {
          this.coreUtilsService.applyIonicColorToRoot(
            'accent',
            data.instanceColor
          );
        })
      ),
    { dispatch: false }
  );

  reapplyAccentOnThemeChange$: Observable<CoreActions> = createEffect(() =>
    new Observable<void>(observer => {
      const root = this.document.documentElement;
      const HC_CLASS = 'ion-palette-high-contrast';
      let prevTheme = root.getAttribute('data-theme');
      let prevHighContrast = root.classList.contains(HC_CLASS);
      // Only react to an actual theme or high-contrast change — `class` carries
      // unrelated toggles too, so emitting on every class mutation would
      // re-apply the accent needlessly.
      const mutationObserver = new MutationObserver(mutations => {
        let changed = false;
        for (const mutation of mutations) {
          if (mutation.attributeName === 'data-theme') {
            const theme = root.getAttribute('data-theme');
            if (theme !== prevTheme) {
              prevTheme = theme;
              changed = true;
            }
          } else if (mutation.attributeName === 'class') {
            const highContrast = root.classList.contains(HC_CLASS);
            if (highContrast !== prevHighContrast) {
              prevHighContrast = highContrast;
              changed = true;
            }
          }
        }
        if (changed) {
          // MutationObserver fires outside Angular's zone; run the emission
          // inside it so the dispatched action reliably triggers change
          // detection for OnPush components on every theme toggle.
          this.ngZone.run(() => observer.next());
        }
      });
      mutationObserver.observe(root, {
        attributes: true,
        attributeFilter: ['data-theme', 'class']
      });
      return () => mutationObserver.disconnect();
    }).pipe(
      switchMap(() =>
        this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
      ),
      filter(instanceInfo => !!(instanceInfo && instanceInfo.instanceColor)),
      map(instanceInfo => {
        // applyIonicColorToRoot derives and returns the theme-aware color;
        // reuse that single derivation instead of computing it twice.
        const themeAwareColor = this.coreUtilsService.applyIonicColorToRoot(
          'accent',
          instanceInfo.instanceColor
        );
        return new CoreActionUpdateInstanceAppColor(themeAwareColor);
      })
    )
  );

  loadInstanceFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOAD_INSTANCE_INFO_FAILED),
        withLatestFrom(this.store$.pipe(select(selectorCoreHttpError))),
        tap(([action, httpError]) => {
          if (httpError.status === 503) {
            /**
             * Clear the CoreHttpError, so that the field error is empty in instance code page
             * To avoid the double error message in popup and instance code input
             */
            const coreHttpError: CoreHttpError = {
              message: void 0,
              name: '',
              status: void 0,
              statusText: ''
            };
            this.store$.dispatch(new CoreHttpActionSetError(coreHttpError));
            this.store$.dispatch(new CoreHttpActionSetMaintenanceMode());
          }
        })
      ),
    { dispatch: false }
  );

  checkAppMinVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.CHECK_APP_MIN_VERSION),
        switchMap(() =>
          from(this.platform.ready()).pipe(
            // retryWithDelay(1000, 30),
            catchError(() => of(void 0))
          )
        ),
        filter(() => this.platform.is('ios') || this.platform.is('android')),
        switchMap(() =>
          this.coreApiService.checkAppMinVersion().pipe(
            map(versionInfo => [
              versionInfo.requiredVersion,
              versionInfo.forceUpdate
            ]),
            catchError(() => of(void 0))
          )
        ),
        tap(([requiredVersion, forceUpdate]) => {
          const currentVersion = this.environment.version;
          const convert = (version: string): number => {
            const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
            return match
              ? parseInt(match[1].padEnd(match[1].length + 8, '0'), 10) +
                  parseInt(match[2].padEnd(match[2].length + 4, '0'), 10) +
                  parseInt(match[3], 10)
              : 0;
          };
          if (
            currentVersion &&
            requiredVersion &&
            this.environment.appStoreLinks &&
            convert(currentVersion) < convert(requiredVersion)
          ) {
            this.loginSubscription$ = this.store$
              .pipe(
                select(selectorCoreLogin),
                filter(login => !!login),
                take(2)
              )
              .subscribe(login => {
                this.store$.dispatch(
                  new CoreActionUpdateUserLanguage({
                    language: login.language,
                    rightToLeft: login.language
                      ? login.language === 'ar'
                      : false
                  })
                );
              });
            forkJoin([
              this.coreUtilsService.getSafeTranslations([
                'APP_UPDATE_POPUP.HEADER',
                'APP_UPDATE_POPUP.MESSAGE.FORCE_UPDATE',
                'APP_UPDATE_POPUP.MESSAGE.SKIP_UPDATE',
                'APP_UPDATE_POPUP.BUTTON.DOWNLOAD',
                'APP_UPDATE_POPUP.BUTTON.SKIP'
              ]),
              this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
            ]).subscribe(([translations, instanceInfo]) => {
              const downloadButton = {
                text: translations[3],
                cssClass: ['bepatient-shared-alert-last-button'],
                handler: () => {
                  const storeLink = this.platform.is('android')
                    ? this.environment.appStoreLinks.android
                    : this.environment.appStoreLinks.ios;
                  if (this.loginSubscription$) {
                    this.loginSubscription$.unsubscribe();
                  }
                  Browser.open({
                    url: encodeURI(storeLink)
                  });
                  return false;
                }
              };
              const skipButton = {
                text: translations[4],
                handler: () => {
                  if (this.loginSubscription$) {
                    this.loginSubscription$.unsubscribe();
                  }
                  return true;
                }
              };
              this.alertController
                .create({
                  mode: 'ios',
                  cssClass: 'bepatient-shared-alert',
                  backdropDismiss: false,
                  header: translations[0],
                  message: forceUpdate ? translations[1] : translations[2],
                  buttons: forceUpdate
                    ? [downloadButton]
                    : [skipButton, downloadButton]
                })
                .then(alert => {
                  alert.present();
                  if (instanceInfo && instanceInfo.appColor) {
                    alert.style.setProperty(
                      '--bepatient-shared-alert-color',
                      instanceInfo.appColor
                    );
                    alert.style.setProperty(
                      '--bepatient-shared-alert-color-contrast',
                      this.sharedUtilsService.contrastColor(
                        instanceInfo.appColor
                      )
                    );
                  }
                });
            });
          }
        })
      ),
    { dispatch: false }
  );

  launchChatbot$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.LAUNCH_CHATBOT),
      withLatestFrom(
        this.store$.pipe(
          select(selectorCoreChatbotAppConfiguration, {
            platform: this.platform.is('android') ? 'android' : 'ios'
          })
        ),
        (action, config) => config
      ),
      map(config => new CoreActionOpenExternalApp(config))
    )
  );

  navigateToResource$: Observable<CoreActions | CoreRouterActions> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.NAVIGATE_TO_RESOURCE),
        map((action: CoreActionNavigateToResource) => action.payload),
        tap(payload => {
          // Extract form ID from resource href if it's a form
          if (
            payload.resource.type === CoreMobiResourceType.FORM &&
            payload.resource.href
          ) {
            const formMatch = payload.resource.href.match(/\/forms\/(\d+)\//);
            if (formMatch) {
              const formId = formMatch[1];
              // Extracted form ID from resource navigation
              this.store$.dispatch(new CoreActionSetCurrentFormId(formId));
            }
          }
        }),
        withLatestFrom(
          this.store$.pipe(select(selectorCoreRouterUrl)),
          this.store$.pipe(select(selectorCorePageModules)),
          this.store$.pipe(select(selectorCoreNavigateToResourceStack)),
          (payload, fromUrl, pageModules, stack) => {
            let toUrl: string;
            let depthModule: CorePageModule;

            switch (payload.resource.type) {
              case CoreMobiResourceType.CATEGORY:
                depthModule = pageModules.find(
                  module => module.ID === CorePageModuleIDs.CATEGORY
                );
                break;
              case CoreMobiResourceType.FACTSHEET:
                depthModule = pageModules.find(
                  module => module.ID === CorePageModuleIDs.FACTSHEET
                );
                break;
              case CoreMobiResourceType.FORM:
                depthModule = pageModules.find(
                  module => module.ID === CorePageModuleIDs.MY_FORM
                );
                break;
              case CoreMobiResourceType.QUIZZ:
                depthModule = pageModules.find(
                  module => module.ID === CorePageModuleIDs.QUIZZ
                );
                break;
            }

            if (depthModule) {
              if (payload.asRoot) {
                toUrl =
                  fromUrl === `/${depthModule.routes[0].path}`
                    ? `/${depthModule.routes[1].path}`
                    : `/${depthModule.routes[0].path}`;

                if (stack.length) {
                  fromUrl = stack[0].fromUrl;
                  this.location.replaceState(fromUrl);
                }
              } else {
                const deeperModule = stack
                  .filter(module => module.type === payload.resource.type)
                  .pop();

                const depth = deeperModule
                  ? depthModule.routes.findIndex(
                      route => `/${route.path}` === deeperModule.toUrl
                    ) + 1
                  : 0;

                toUrl =
                  depth < CoreUtils.DEPTH_MODULE_MAX_DEPTH
                    ? `/${depthModule.routes[depth].path}`
                    : void 0;
              }
            }

            return {
              toUrl,
              fromUrl,
              payload
            };
          }
        ),
        map(data => {
          switch (data.payload.resource.type) {
            case CoreMobiResourceType.CHATBOT:
              return new CoreActionLaunchChatbot();

            case CoreMobiResourceType.BODY_SCHEMA:
              return new CoreActionNavigateToSingleResource({
                href: data.payload.resource.href,
                asRoot: data.payload.asRoot,
                pageModuleID: CorePageModuleIDs.BODY_SCHEMA
              });

            case CoreMobiResourceType.MEASURE:
              return new CoreActionLoadMeasure({
                href: data.payload.resource.href,
                isBlocking: data.payload.isBlocking
              });

            case CoreMobiResourceType.FORM:
            case CoreMobiResourceType.QUIZZ:
            case CoreMobiResourceType.CATEGORY:
            case CoreMobiResourceType.FACTSHEET:
              return data.toUrl
                ? new CoreActionNavigateToResourceStack({
                    resource: {
                      type: data.payload.resource.type,
                      href: data.payload.resource.href,
                      toUrl: data.toUrl,
                      fromUrl: data.fromUrl
                    },
                    asRoot: data.payload.asRoot
                  })
                : new CoreActionNOOP();

            case CoreMobiResourceType.DEEPLINK:
              console.log('DEEPLINK Navigation Triggered:', {
                originalHref: data.payload.resource.href,
                pageModuleID: CorePageModuleIDs.BRIDGE_WEBVIEW
              });

              // Device detection for deeplink behavior using Capacitor
              const isNativePlatformDeeplink = Capacitor.isNativePlatform();
              const isMobileDeviceDeeplink =
                isNativePlatformDeeplink ||
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                  navigator.userAgent
                );
              console.log('Deeplink Device Detection (Capacitor):', {
                isNativePlatform: isNativePlatformDeeplink,
                isMobileDevice: isMobileDeviceDeeplink,
                capacitorPlatform: Capacitor.getPlatform(),
                userAgent: navigator.userAgent,
                ionicPlatforms: this.platform.platforms()
              });

              if (isMobileDeviceDeeplink) {
                // Mobile/tablet: Use Capacitor Browser (external browser)
                console.log(
                  'Mobile device detected - Opening deeplink in external browser'
                );
                Browser.open({
                  url: data.payload.resource.href,
                  windowName: '_system'
                });
                return new CoreActionNOOP();
              } else {
                // Desktop/laptop: Use iframe approach (modal)
                console.log(
                  'Desktop device detected - Using iframe modal for deeplink'
                );
                const webviewUrl = `/bridge-webview`;
                console.log('Deeplink URL:', webviewUrl);

                return new CoreRouterActionNavigateForward({
                  url: webviewUrl,
                  options: {
                    queryParams: {
                      url: data.payload.resource.href,
                      title: 'External Link',
                      target: 'pvt' // Default target for this context
                    },
                    animationDirection: 'forward'
                  } as any
                });
              }

            default:
              return new CoreActionNOOP();
          }
        })
      )
    );

  navigateToSingleResource: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.NAVIGATE_TO_SINGLE_RESOURCE),
      map(
        (action: CoreActionNavigateToSingleResource) =>
          new CoreRouterActionNavigateToPageModule({
            id: action.payload.pageModuleID,
            intent: action.payload.asRoot
              ? void 0
              : CoreRouterNavigationIntent.FORWARD
          })
      )
    )
  );

  navigateToResourceStack$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.NAVIGATE_TO_RESOURCE_STACK),
      map((action: CoreActionNavigateToResourceStack) =>
        action.payload.asRoot
          ? new CoreRouterActionNavigateRoot({
              url: action.payload.resource.toUrl
            })
          : new CoreRouterActionNavigateForward({
              url: action.payload.resource.toUrl
            })
      )
    )
  );

  navigateBackFromResource$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      map((action: RouterRequestAction) => [
        action.payload.routerState.url,
        action.payload.event.url
      ]),
      withLatestFrom(
        this.store$.pipe(
          select(selectorCoreNavigateToResourceStackLastItemType())
        ),
        (routes, item) => ({
          item,
          to: routes[1],
          from: routes[0]
        })
      ),
      filter(
        data =>
          data.item &&
          data.item.toUrl === data.from &&
          data.item.fromUrl === data.to
      ),
      map(() => new CoreActionNavigateToResourceStackPop())
    )
  );

  navigatedToPageModule$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map((action: any) => <string>action.payload.routerState.parentRoutePath),
      withLatestFrom(this.store$.pipe(select(selectorCorePageModules))),
      switchMap(([parentRoutePath, modules]) => {
        let routeIndex = -1;
        const foundModule = modules.find(module => {
          routeIndex = module.routes.findIndex(
            route => parentRoutePath === route.path
          );
          return routeIndex > -1;
        });

        return foundModule
          ? of(
              new CoreActionNavigatedToPageModule({
                moduleID: foundModule.ID,
                routeIndex: routeIndex
              })
            )
          : EMPTY;
      })
    )
  );

  // Capture form ID from navigation
  captureFormIdFromNavigation$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map((action: any) => action.payload.routerState.url),
      filter(url => url && url.includes('/forms/')),
      map(url => {
        // Extract form ID from URL patterns like /forms/17465/answers/17465
        const formMatch = url.match(/\/forms\/(\d+)\//);
        return formMatch ? formMatch[1] : null;
      }),
      filter(formId => formId !== null),
      map(formId => new CoreActionSetCurrentFormId(formId))
    )
  );

  gotoNextActivity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.GOTO_NEXT_ACTIVITY),
        map((action: CoreActionGotoNextActivity) => action.payload),
        filter(payload => !!payload),
        tap(payload => {
          this.store$.dispatch(
            new CoreActionNavigateToResource({
              resource: {
                type: payload.type,
                href: payload.href
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  menuAvailable = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.NAVIGATED_TO_PAGE_MODULE),
        switchMap((action: CoreActionNavigatedToPageModule) =>
          this.store$.pipe(
            selectCorePageModule(action.payload.moduleID),
            map(module => [action, module])
          )
        ),
        withLatestFrom(
          this.store$.pipe(select(selectorCoreNavigateToResourceStack))
        ),
        tap(
          ([[action, module], stack]: [
            [CoreActionNavigatedToPageModule, CorePageModule],
            Array<CoreResourceStackItem>
          ]) => {
            this.menuController.enable(
              (module.leftMenuAvailableForDepthModules && stack.length >= 1) ||
                (module.leftMenuAvailableForPages &&
                  module.leftMenuAvailableForPages.indexOf(
                    action.payload.routeIndex
                  ) > -1),
              'leftMenu'
            );
            this.menuController.enable(
              (module.rightMenuAvailableForDepthModules &&
                stack.length === 1) ||
                (module.rightMenuAvailableForPages &&
                  module.rightMenuAvailableForPages.indexOf(
                    action.payload.routeIndex
                  ) > -1),
              'rightMenu'
            );
          }
        )
      ),
    { dispatch: false }
  );

  sendFCMToken = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGIN_SUCCESS),
        tap(() => {
          this.store$.pipe(select(selectorCoreLogin)).subscribe(login => {
            this.jwtCheckFrequency = login
              ? login.tokenValidationCheckFrequency * 1000
              : 60000;
            this.jwtWarningExpirationDuration = login
              ? login.tokenWarningExpirationDuration / 60
              : 30;
          });
        })
      ),
    { dispatch: false }
  );

  fcmTokenRefresh = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.FCM_TOKEN_REFRESH),
        map((action: CoreActionFCMTokenRefresh) => action.payload),
        switchMap(fcmToken =>
          this.coreApiService.refreshFCMToken(fcmToken).pipe(
            map(data => {
              delay(500);
              this.toast(data.message, true);
              this.store$.dispatch(
                new CoreActionUpdateLogin({ isNewDevice: false })
              );
              return new CoreActionFCMTokenRefreshSuccess();
            }),
            catchError(error => {
              delay(500);
              this.toast(error.error.message, false);
              return of(new CoreActionFCMTokenRefreshFailed());
            })
          )
        )
      ),
    { dispatch: false }
  );

  checkForNewDevice = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.CHECK_NEW_DEVICE),
        filter(() => this.platform.is('hybrid')),
        delay(1000),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'NEW_DEVICE_NOTIFICATION.MESSAGE',
              'NEW_DEVICE_NOTIFICATION.BUTTON.YES',
              'NEW_DEVICE_NOTIFICATION.BUTTON.NO'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
            this.store$.pipe(select(selectorCoreFCMToken), take(1)),
            this.store$.pipe(select(selectorCoreLogin), take(1))
          ])
        ),
        tap(([translations, instanceInfo, fcmToken, login]) => {
          this.alertController
            .create({
              mode: 'ios',
              cssClass: 'bepatient-notification-alert',
              backdropDismiss: false,
              message: login.popupNewDeviceMessage || translations[0],
              buttons: [
                {
                  text: translations[2],
                  cssClass: 'bepatient-alert-single-button',
                  handler: () => {
                    // If user disagree the trusted device popup, set boolen
                    // To check and disable popup for current session.
                    this.store$.dispatch(
                      new CoreActionDisagreeTrustedDevice(true)
                    );
                  },
                  role: 'cancel'
                },
                {
                  text: translations[1],
                  cssClass: 'bepatient-alert-last-button',
                  handler: () => {
                    this.store$.dispatch(
                      new CoreActionFCMTokenRefresh(fcmToken)
                    );
                    this.store$.dispatch(
                      new CoreActionDisagreeTrustedDevice(false)
                    );
                  },
                  role: 'ok'
                }
              ]
            })
            .then(alert => {
              alert.present();
              if (instanceInfo && instanceInfo.appColor) {
                alert.style.setProperty(
                  '--bepatient-notification-alert-color-primary',
                  instanceInfo.appColor
                );
              }
            });
        })
      ),
    { dispatch: false }
  );

  formOpenExternalTarget$: Observable<CoreActions | CoreRouterActions> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.FORM_OPEN_EXTERNAL_TARGET),
        map((action: CoreActionFormOpenExternalTarget) => action.payload),
        filter(payload => payload.target),
        withLatestFrom(
          this.store$.select(selectorCoreLoadedFormData),
          (payload, formData) => ({ payload, formData })
        ),
        switchMap(({ payload, formData }) => {
          console.log('Form button clicked:', {
            target: payload.target,
            href: payload.href,
            label: payload.label,
            uniqueKey: payload.uniqueKey
          });
          // MyFSHD is not available in iOS. Return if opened in iphone/ipad
          if (
            payload.target === 'deeplink' &&
            payload.href.includes('com.bepatient.myfshd') &&
            this.platform.is('ios')
          ) {
            return new Observable<CoreActions>(subscriber => {
              subscriber.next(new CoreActionFormOpenExternalTargetInvalid());
            });
          }
          // If the href is a valid APP deeplink, open the APP
          if (
            payload.target === 'deeplink' &&
            payload.href.includes('com.bepatient')
          ) {
            const selectorCoreExtraApp = selectCoreExtraAppConfiguration({
              deviceKey: payload.href,
              devType: this.environment?.devType?.toLowerCase() || void 0,
              bundleId: payload.href || void 0,
              bundleURL:
                payload.href + `${this.platform.is('ios') ? '://' : ''}`,
              activityId: (formData && formData?.uuid) || void 0,
              occurrence: (formData && formData?.intPageOccurrence) || void 0
            });

            return this.store$.pipe(
              select(selectorCoreExtraApp),
              take(1),
              switchMap(
                config =>
                  new Observable<CoreActions>(subscriber => {
                    subscriber.next(new CoreActionOpenExternalApp(config));
                  })
              )
            );
          }
          // Handle external app target with device-specific behavior
          if (payload.target) {
            // Validate target is allowed
            if (!this.coreExternalAppService.isTargetAllowed(payload.target)) {
              console.error(`Target '${payload.target}' is not allowed`);
              return of(new CoreActionNOOP());
            }

            console.log('External app assessment opened:', {
              href: payload.href,
              target: payload.target
            });

            // Device detection for deeplink behavior using Capacitor
            const isNativePlatform = Capacitor.isNativePlatform();
            const isMobileDevice =
              isNativePlatform ||
              /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
              );
            console.log('Form Deeplink Device Detection (Capacitor):', {
              isNativePlatform,
              isMobileDevice,
              capacitorPlatform: Capacitor.getPlatform(),
              userAgent: navigator.userAgent,
              ionicPlatforms: this.platform.platforms()
            });

            if (isMobileDevice) {
              // Mobile/tablet: Use Capacitor Browser (external browser)
              console.log('External browser opened for mobile device');
              return new Observable<CoreActions>(subscriber => {
                // Apply dynamic URL building for external app assessments
                this.store$
                  .pipe(select(selectorCoreLogin), take(1))
                  .subscribe(loginData => {
                    this.coreUtilsService
                      .buildDynamicExternalAppUrl(
                        payload.href,
                        loginData,
                        formData,
                        this.environment,
                        true, // isMobileDevice
                        payload.target // target
                      )
                      .then(url => {
                        // Extract userId from loginData
                        const userId =
                          this.coreUtilsService.extractUserIdFromToken(
                            loginData.iAuth
                          );
                        if (userId === null) {
                          console.warn(
                            'No user ID available for uniqueKey generation'
                          );
                          return;
                        }
                        // Use the uniqueKey from payload (already includes fieldId)
                        const uniqueKey = payload.uniqueKey;

                        // Track external app assessment for mobile browser close handling
                        this.coreExternalAppService.trackExternalAppAssessmentForMobile(
                          uniqueKey,
                          {
                            url: url,
                            originalUrl: payload.href,
                            formData: formData,
                            openedAt: new Date().toISOString()
                          }
                        );

                        // Set up browser event listeners for external app assessment completion detection
                        this.coreExternalAppService.setupExternalAppBrowserListeners(
                          uniqueKey
                        );

                        console.log(
                          'In-App browser opened for external app assessment'
                        );

                        // Use in-app browser first - it handles 302 redirects automatically
                        Browser.open({
                          url: url,
                          presentationStyle: 'fullscreen'
                        })
                          .then(() => {
                            subscriber.next(new CoreActionNOOP());
                          })
                          .catch(error => {
                            console.error(
                              'Error opening in-app browser:',
                              error
                            );
                            // Fallback to App Launcher if in-app browser fails
                            console.log('External browser opened (fallback)');
                            AppLauncher.openUrl({ url: url })
                              .then(() => {
                                subscriber.next(new CoreActionNOOP());
                              })
                              .catch(fallbackError => {
                                console.error(
                                  'Error opening external browser (fallback):',
                                  fallbackError
                                );
                                subscriber.next(new CoreActionNOOP());
                              });
                          });
                      });
                  });
              });
            } else {
              // Desktop/laptop: Use iframe approach (modal)
              console.log(
                'Desktop device detected - Using iframe modal for deeplink'
              );
              return new Observable<CoreRouterActions>(subscriber => {
                const webviewUrl = `/bridge-webview`;
                subscriber.next(
                  new CoreRouterActionNavigateForward({
                    url: webviewUrl,
                    options: {
                      queryParams: {
                        url: payload.href,
                        title: payload.label || 'External Link',
                        uniqueKey: payload.uniqueKey || 'unknown',
                        target: payload.target || 'pvt'
                      },
                      animationDirection: 'forward'
                    } as any
                  })
                );
              });
            }
            return new Observable<CoreActions>(subscriber => {
              subscriber.next(new CoreActionNOOP());
            });
          }

          // If href is Web URL, Open Browser
          // regex to check if the href string is a valid url
          const urlRegex = new RegExp(
            '^(https?:\\/\\/)?' + // protocol
              '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
              '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
              '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
              '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
              '(\\#[-a-z\\d_]*)?$',
            'i'
          );
          // If the href is a valid url, open browser to show the url
          if (
            !payload?.href?.includes('com.bepatient') &&
            urlRegex.test(payload.href)
          ) {
            return new Observable<CoreActions>(subscriber => {
              subscriber.next(new CoreActionOpenExternalLink(payload.href));
            });
          }
          // Else do nothing
          return new Observable<CoreActions>(subscriber => {
            subscriber.next(new CoreActionNOOP());
          });
        })
      )
    );

  openExternalApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.OPEN_EXTERNAL_APP),
      map((action: CoreActionOpenExternalApp) => action.payload),
      switchMap(externalAppConfiguration =>
        this.coreExternalAppService.launchApp(externalAppConfiguration).pipe(
          map(() => new CoreActionOpenExternalAppSuccess()),
          catchError(e => of(new CoreActionOpenExternalAppFailed(e)))
        )
      )
    )
  );

  openExternalAppFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.OPEN_EXTERNAL_APP_FAILED),
        map((action: CoreActionOpenExternalAppFailed) => action.payload),
        switchMap(error =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'MEASURE_APP_INSTALL.TITLE',
              'MEASURE_APP_INSTALL.MESSAGE',
              'MEASURE_APP_INSTALL.CANCEL',
              'MEASURE_APP_INSTALL.DOWNLOAD',
              'MEASURE_APP_INSTALL.MESSAGE_WEBVIEW',
              'MEASURE_APP_INSTALL.MESSAGE_KYOMED'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
            of(error)
          ])
        ),
        tap(([translations, instanceInfo, error]) => {
          const isNativePlatform = Capacitor.isNativePlatform();
          this.sharedPopupService
            .simpleAlert({
              color: instanceInfo && instanceInfo['appColor'],
              id:
                (error && error.error && error.error.toString()) ||
                'Application not found',
              title: translations[0],
              message: isNativePlatform
                ? error.app === 'com.bepatient.smartiot'
                  ? translations[1]
                  : translations[5]
                : translations[4],
              buttons: isNativePlatform
                ? [translations[2], translations[3]]
                : [translations[2]]
            })
            .then(index => {
              const getAppStoreLink = (bundleID: string): string => {
                let appStoreLink;
                if (bundleID.includes('com.bepatient.smartiot')) {
                  appStoreLink = this.platform.is('android')
                    ? 'market://details?id=com.bepatient.smartiot'
                    : 'itms-apps://itunes.apple.com/app/id1626224379';
                } else if (bundleID.includes('com.bepatient.myfshd')) {
                  // TODO app store link for kyomed
                  appStoreLink = this.platform.is('android')
                    ? 'market://details?id=com.bepatient.myfshd'
                    : 'itms-apps://itunes.apple.com/app/id1626224379';
                }
                return appStoreLink ? encodeURI(appStoreLink) : void 0;
              };

              // Redirect user to app store for installing App
              if (index === 1) {
                const storeLink = getAppStoreLink(error.app);
                if (storeLink) {
                  AppLauncher.openUrl({ url: storeLink });
                }
              }
            });
        })
      ),
    { dispatch: false }
  );

  openExternalAppTargetInvalid$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.FORM_OPEN_EXTERNAL_TARGET_INVALID),
        switchMap(() =>
          forkJoin([
            this.coreUtilsService.getSafeTranslations([
              'MEASURE_APP_INSTALL.TITLE',
              'MEASURE_APP_INSTALL.MESSAGE_IOS',
              'MEASURE_APP_INSTALL.CANCEL'
            ]),
            this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
          ])
        ),
        tap(([translations, instanceInfo]) => {
          this.sharedPopupService.simpleAlert({
            color: instanceInfo && instanceInfo['appColor'],
            id: 'Application not compatible',
            title: translations[0],
            message: translations[1],
            buttons: [translations[2]]
          });
        })
      ),
    { dispatch: false }
  );

  checkJWTAfterPlatformResume$: Observable<any> = createEffect(
    () =>
      merge(
        this.coreUtilsService.appStateActive,
        timer(0, this.jwtCheckFrequency)
      ).pipe(tap(() => this.store$.dispatch(new CoreActionCheckForJWT()))),
    { dispatch: false }
  );

  checkForJwtAfterInternetConnection$: Observable<CoreActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreHttpActionsType.INTERNET_IS_WORKING),
        tap(() => this.store$.dispatch(new CoreActionCheckForJWT()))
      ),
    { dispatch: false }
  );

  checkForJWT: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.CHECK_FOR_JWT),
      withLatestFrom(
        this.store$.pipe(select(selectorCoreLogin)),
        this.store$.pipe(select(selectorCoreHttpInternetConnection)),
        this.idle$,
        this.store$.pipe(select(selectorCoreIsAppActive)),
        (i, login, internet, userIdle, appStateActive) => ({
          iAuth: login && login.iAuth,
          internet,
          userIdle,
          appStateActive
        })
      ),
      filter(data => !!data.iAuth && (data.internet || navigator.onLine)),
      map(data => {
        const decoded = jwtDecode<JwtPayload>(data.iAuth);
        const minutes = (decoded.exp - Math.round(Date.now() / 1000)) / 60;
        take(this.jwtCheckFrequency);

        // Parse the Token date using the Unix timestamp and format it to year, month and day
        const tokenExpiryDateTime = fromUnixTime(decoded.exp) || void 0;
        const year = tokenExpiryDateTime
          ? format(tokenExpiryDateTime, 'yyyy')
          : void 0;
        const month = tokenExpiryDateTime
          ? format(tokenExpiryDateTime, 'MM')
          : void 0;
        const day = tokenExpiryDateTime
          ? format(tokenExpiryDateTime, 'dd')
          : void 0;

        // Create a JS Time object using the year, month and day for token and today date
        const tokenDate =
          year && month && day
            ? new Date(Number(year), Number(month) - 1, Number(day))
            : void 0;
        const todayDate =
          new Date(
            Number(format(new Date(), 'yyyy')),
            Number(format(new Date(), 'MM')) - 1,
            Number(format(new Date(), 'dd'))
          ) || void 0;

        if (minutes < 1 && data.appStateActive) {
          // Check if the tokenDate is before todayDate and if the token is expired to remove login and popup if exists
          if (tokenDate && todayDate && isBefore(tokenDate, todayDate)) {
            // Dismiss the  Session Expired popup if exists
            if (this.sharedPopupService.exist('Popup has expired')) {
              this.sharedPopupService.dismiss('Popup has expired');
            }
            return new CoreActionRemoveLogin();
          }
          return new CoreActionJwtHasExpired();
        } else if (
          minutes > 0 &&
          minutes < this.jwtWarningExpirationDuration &&
          !data.userIdle
        ) {
          // If user is not idle (Active) and reached jwt warning expiration time, refresh token
          return new CoreActionRegenerateToken();
        } else {
          return new CoreActionJwtIsValid();
        }
      })
    )
  );

  jwtHasExpired$: Observable<CoreActionLogout> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.JWT_HAS_EXPIRED),
      filter(
        () =>
          !this.sharedPopupService.exist(JWTPopupID.HAS_EXPIRED) &&
          !(localStorage.getItem('sessionPopup') === JWTPopupID.HAS_EXPIRED)
      ),
      tap(() => this.sharedPopupService.dismiss(JWTPopupID.WILL_EXPIRE)),
      switchMap(() =>
        forkJoin([
          this.coreUtilsService.getSafeTranslations([
            'TIMEOUT_POPUP.TITLE',
            'TIMEOUT_POPUP.SESSION_EXPIRED'
          ]),
          this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
        ])
      ),
      switchMap(([translations, instanceInfo]) =>
        from(
          this.sharedPopupService.simpleAlert({
            color: instanceInfo && instanceInfo.appColor,
            id: JWTPopupID.HAS_EXPIRED,
            title: translations[0],
            message: translations[1],
            buttons: [this.translate.instant('OK')]
          })
        )
      ),
      map(() => new CoreActionLogout())
    )
  );

  jwtWillExpire$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.JWT_WILL_EXPIRE),
      filter(
        () =>
          !this.sharedPopupService.exist(JWTPopupID.WILL_EXPIRE) &&
          !(localStorage.getItem('sessionPopup') === JWTPopupID.WILL_EXPIRE)
      ),
      switchMap(() =>
        forkJoin([
          this.coreUtilsService.getSafeTranslations([
            'TIMEOUT_POPUP.TITLE',
            'TIMEOUT_POPUP.MESSAGE',
            'TIMEOUT_POPUP.LOGOUT_LABEL',
            'TIMEOUT_POPUP.STAY_CONNECTED_LABEL'
          ]),
          this.store$.pipe(select(selectorCoreInstanceInfo), take(1))
        ])
      ),
      switchMap(([translations, instanceInfo]) =>
        from(
          this.sharedPopupService.simpleAlert({
            color: instanceInfo && instanceInfo.appColor,
            id: JWTPopupID.WILL_EXPIRE,
            title: translations[0],
            message: translations[1],
            buttons: [translations[2], translations[3]]
          })
        )
      ),
      map(index =>
        index ? new CoreActionRegenerateToken() : new CoreActionLogout()
      )
    )
  );

  jwtIsValid = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.JWT_IS_VALID),
        tap(() => {
          localStorage.setItem('sessionPopup', '');
        })
      ),
    { dispatch: false }
  );

  logout = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGOUT),
        switchMap(() =>
          this.coreApiService.logout().pipe(
            map(status => {
              this.sharedPopupService.dismissAll();
              localStorage.setItem('sessionPopup', '');
              // Dispatching Logout success if res is true
              if (status) {
                this.store$.dispatch(new CoreActionLogoutSuccess());
              }
              this.store$.dispatch(new CoreActionCheckAppMinVersion());
            }),
            catchError(() => of(new CoreActionLogoutFailed()))
          )
        )
      ),
    { dispatch: false }
  );

  logoutSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGOUT_SUCCESS),
        tap(() => {
          this.store$.dispatch(
            new CoreRouterActionNavigateRoot({ url: '/onboarding-login' })
          );
        })
      ),
    { dispatch: false }
  );

  loginFailed = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.LOGIN_FAILED),
        tap(() => {
          // this.googleAnalyticsEventsService.emitEvent(
          //   coreAnalyticsEvents.CONNECTION_FAIL
          // )
        })
      ),
    { dispatch: false }
  );

  privacyRead = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.PRIVACY_SUCCESS),
        tap(() => localStorage.setItem('isPrivacyRead', 'true'))
      ),
    { dispatch: false }
  );

  /**
   * Save isOnceLoggedIn in localStorage when user login success.
   * Later to check it and redirect user to user-id page instead of login page with options.
   */
  saveOnceLoggedUser = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CoreActionsTypes.LOGIN_SUCCESS,
          CoreActionsTypes.SAVE_LOGIN_CHECK
        ),
        withLatestFrom(this.store$.pipe(select(selectorCoreLogin))),
        tap(([, login]) => {
          let isOncelogged = localStorage.getItem('isOnceLoggedIn') || void 0;
          if (!isOncelogged) {
            localStorage.setItem('isOnceLoggedIn', 'true');
          }

          // Set default locale and options for Date-fns based on user language
          const dateFnsLocaleId =
            resolveCoreLanguage(login.language).dateFnsLocaleId ?? 'enUS';
          switch (dateFnsLocaleId) {
            case 'de':
              setDefaultOptions({ locale: de, weekStartsOn: 1 });
              break;
            case 'fr':
              setDefaultOptions({ locale: fr, weekStartsOn: 1 });
              break;
            case 'frCA':
              setDefaultOptions({ locale: frCA, weekStartsOn: 1 });
              break;
            case 'es':
              setDefaultOptions({ locale: es, weekStartsOn: 1 });
              break;
            case 'fi':
              setDefaultOptions({ locale: fi, weekStartsOn: 1 });
              break;
            case 'hr':
              setDefaultOptions({ locale: hr, weekStartsOn: 1 });
              break;
            case 'hu':
              setDefaultOptions({ locale: hu, weekStartsOn: 1 });
              break;
            case 'nl':
              setDefaultOptions({ locale: nl, weekStartsOn: 1 });
              break;
            case 'nlBE':
              setDefaultOptions({ locale: nlBE, weekStartsOn: 1 });
              break;
            case 'pl':
              setDefaultOptions({ locale: pl, weekStartsOn: 1 });
              break;
            case 'pt':
              setDefaultOptions({ locale: pt, weekStartsOn: 1 });
              break;
            case 'ptBR':
              setDefaultOptions({ locale: ptBR, weekStartsOn: 1 });
              break;
            case 'ro':
              setDefaultOptions({ locale: ro, weekStartsOn: 1 });
              break;
            case 'sv':
              setDefaultOptions({ locale: sv, weekStartsOn: 1 });
              break;
            case 'zhCN':
              setDefaultOptions({ locale: zhCN, weekStartsOn: 1 });
              break;
            case 'zhTW':
              setDefaultOptions({ locale: zhTW, weekStartsOn: 1 });
              break;
            case 'enAU':
              setDefaultOptions({ locale: enAU, weekStartsOn: 1 });
              break;
            case 'enCA':
              setDefaultOptions({ locale: enCA, weekStartsOn: 1 });
              break;
            case 'enGB':
              setDefaultOptions({ locale: enGB, weekStartsOn: 1 });
              break;
            case 'enNZ':
              setDefaultOptions({ locale: enNZ, weekStartsOn: 1 });
              break;
            case 'deAT':
              setDefaultOptions({ locale: deAT, weekStartsOn: 1 });
              break;
            case 'ja':
              setDefaultOptions({ locale: ja, weekStartsOn: 1 });
              break;
            case 'it':
              setDefaultOptions({ locale: it, weekStartsOn: 1 });
              break;
            case 'enUS':
            default:
              setDefaultOptions({ locale: enUS, weekStartsOn: 1 });
              break;
          }
        })
      ),
    { dispatch: false }
  );

  openExternalLink$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.OPEN_EXTERNAL_LINK),
        map((action: CoreActionOpenExternalLink) => action.payload),
        tap(externalWebLink => {
          this.coreUtilsService.navigateToExternalUrl(externalWebLink);
        })
      ),
    { dispatch: false }
  );

  setBiometricData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.SET_BIOMETRIC_SECRET),
        map((action: CoreActionSetBiometricSecret) => action.payload),
        filter(payload => payload && payload.username && payload.password),
        tap(async userData => {
          // Never store biometric credentials on a compromised device.
          if (await this.biometric.isDeviceRooted()) {
            await this.biometric.disableBiometric('rooted');
            this.coreUtilsService.presentToaster(
              this.translate.instant('BIOMETRIC.ERROR.DEVICE_COMPROMISED')
            );
            return;
          }

          let biometricData = this.biometric.getDeviceBiometricData();

          // Respect an explicit user opt-out: never auto-enrol or prompt.
          if (biometricData?.userOptedOut) {
            return;
          }

          // Replace any previous secret, then store the new one.
          const registerBiometricSecret = (
            secret,
            isBiometricEnabled = 'true'
          ) =>
            this.biometric
              .removeBiometricCredential()
              .then(() => this.biometric.registerBiometricSecret(secret))
              .then(() => {
                // Biometric secret registered in keychain/keystore.
                if (biometricData) {
                  biometricData.keychainEnrolled = 'true';
                  biometricData.permission = 'true';
                  biometricData.enableBiometricLogin =
                    isBiometricEnabled === 'true' ? 'true' : 'false';
                  biometricData.userOptedOut = false;
                  this.biometric.setDeviceBiometricData(biometricData);
                }
              })
              .catch(registerError => {
                // Biometric secret registration failed.
                if (
                  typeof registerError == 'string' ||
                  (registerError && registerError.message)
                )
                  this.coreUtilsService.presentToaster(
                    (registerError && registerError.message) || registerError
                  );

                if (biometricData) {
                  biometricData.keychainEnrolled = 'false';
                  biometricData.permission = 'false';
                  biometricData.enableBiometricLogin = 'disabled';
                  this.biometric.setDeviceBiometricData(biometricData);
                }
              });

          // If permission not granted show Prompt to enable biometric login, and
          if (
            biometricData &&
            (biometricData.permission === 'false' ||
              biometricData.keychainEnrolled === 'false') &&
            biometricData.biometricType != 'none'
          ) {
            this.biometric
              .showBiometricPrompt()
              .then(() => {
                registerBiometricSecret(userData);
              })
              .catch(error => {
                if (error && (!error.code || error.code === '1')) {
                  biometricData = {
                    ...biometricData,
                    enableBiometricLogin: 'disabled',
                    keychainEnrolled: 'false'
                  };
                  this.biometric.setDeviceBiometricData(biometricData);
                }
              });
          } else if (biometricData && biometricData.biometricType != 'none') {
            // Else if permission granted, register biometric secret
            registerBiometricSecret(
              userData,
              biometricData.enableBiometricLogin
            );
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Track user interaction event with the app
   */
  userTouchActivity$: Observable<any> = createEffect(
    () =>
      merge(
        fromEvent(this.document, 'touchstart'), // For touch event (mobile)
        fromEvent(this.document, 'click'), // For mouse event (desktop)
        fromEvent(this.document, 'wheel') // For mouse wheel event (desktop)
      ).pipe(
        debounceTime(500),
        tap(event => {
          this.idle$.next(false);
          // Is idle and interacting, emit idle false
          if (this.isIdle) {
            this.isIdle = false;
            this.idle$.next(false);
          }

          // User interaction, reset start-idle-timer
          clearTimeout(this.countDown);
          this.countDown = setTimeout(() => {
            // Countdown completes without interaction - emit Idle : true
            this.isIdle = true;
            this.idle$.next(true);
          }, this.idleAfterSeconds * 1000);
        })
      ),
    { dispatch: false }
  );

  /**
   * User reset password, update the password in keychain
   */
  setBiometricPass$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.SET_BIOMETRIC_PASSWORD),
        map((action: CoreActionSetBiometricPassword) => action.payload),
        filter(
          payload =>
            !!payload.password &&
            Capacitor.isNativePlatform() &&
            this.biometric.getDeviceBiometricData()?.enableBiometricLogin ===
              'true'
        ),
        tap(data => {
          const newPass = data?.password;
          this.biometric
            .loadBiometricSecret()
            .then(result => {
              if (result && result.username && result.password) {
                this.biometric
                  .registerBiometricSecret({
                    username: result.username,
                    password: newPass
                  })
                  .catch(e => e);
              }
            })
            .catch(e => e);
        })
      ),
    { dispatch: false }
  );

  // Handle app resume for external app assessment completion detection on mobile devices
  externalAppMobileBrowserCloseHandler$ = createEffect(
    () =>
      from(this.platform.resume).pipe(
        filter(() => this.platform.is('hybrid') || this.platform.is('mobile')),
        tap(() => {
          console.log('App resumed');
          // Removed auto-processing on app resume to prevent unwanted form submission
        })
      ),
    { dispatch: false }
  );

  // Check device battery level and show low battery alert if needed (once per session)
  checkBattery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.CHECK_BATTERY),
      delay(2000),
      switchMap(() =>
        forkJoin([
          this.store$.pipe(select(selectorCoreInstanceInfo), take(1)),
          this.store$.pipe(select(selectorLowBatteryCheckComplete), take(1))
        ])
      ),
      switchMap(([instanceInfo, isBatteryChecked]) =>
        from(this.batteryUtilsService.checkBattery(isBatteryChecked)).pipe(
          switchMap(batteryResult => {
            if (!batteryResult.shouldShowAlert) {
              return of(new CoreActionNOOP());
            }

            return from(this.batteryUtilsService.checkBattery(true)).pipe(
              switchMap(freshBatteryInfo =>
                this.batteryAlertService.showBatteryAlert(
                  instanceInfo?.appColor || '',
                  freshBatteryInfo.isCharging
                )
              )
            );
          }),
          catchError(() => of(new CoreActionNOOP()))
        )
      )
    )
  );

  /**
   * Present toast notification
   */
  private async presentToaster(
    message: string,
    duration: number = 2000
  ): Promise<void> {
    try {
      const toast = await this.toastController.create({
        message: message,
        duration: duration,
        position: 'top',
        cssClass: 'custom-toast'
      });
      await toast.present();
    } catch (error) {
      console.error('Error presenting toast:', error);
    }
  }

  private toast(message: string, success: boolean): void {
    this.toastController
      .create({
        message: message,
        position: 'top',
        duration: 2000,
        cssClass: 'bepatient_dynamic_form_save_toast'
      })
      .then(toast => toast.present());
  }
}
